/**
 * UUID v4 generation, shared by the session id ({@link ./session.js}) and the
 * pseudonymous end-user id ({@link ./user.js}).
 *
 * Leaf module (no SDK imports) so any module can use it without a cycle. Prefers
 * Web Crypto, degrades gracefully, and NEVER throws — the SDK must never break the
 * host page, so a missing/insecure `crypto` falls through to a weaker source
 * rather than escaping.
 */

/** Build a UUID v4, preferring crypto; falls back gracefully (documented). */
export function generateUuid(): string {
  const c: Crypto | undefined = globalThis.crypto
  // `crypto.randomUUID` requires a secure context (https or localhost). Detection
  // tests presence, not callability — guard the call so a throwing implementation
  // falls through instead of escaping (the module must never throw).
  if (c && typeof c.randomUUID === 'function') {
    try {
      return c.randomUUID()
    } catch {
      // fall through to getRandomValues
    }
  }
  if (c && typeof c.getRandomValues === 'function') {
    const bytes = c.getRandomValues(new Uint8Array(16))
    bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40 // version 4
    bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80 // variant 10
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
  }
  // Last-resort, non-crypto fallback (SSR / no Web Crypto). Not cryptographically
  // strong — acceptable only because no real entropy source exists here.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0
    const v = ch === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}
